import { Box, ToggleButton, ToggleButtonGroup, capitalize } from '@mui/material';
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { MarketGroupDisplayType, MarketGroup } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { createEditColumn, createIsActiveColumn, createSportIconColumn } from '../../helpers/columns';
import { defaultColumnsMarketGroups } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../utils';
import MarketGroupsPositionInput from '../atoms/MarketGroupsPositionInput';
import MarketGroupDetails from '../organisms/MarketGroupDetails';
import TableTemplate from '../templates/TableTemplate';
import MobileDialogWrapper from '../organisms/MobileDialogWrapper';
import { MARKET_TYPE_GROUP_DISPLAY_TYPE } from '../../constants/enums';
import API_ROUTES from '../../constants/apiRoutes';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG } from '../../constants/featureFlags';

const makeStyles = (selectedRow: boolean) => ({
  container: {
    display: 'flex',
    height: '100%',
    minHeight: 0,
    gap: '1.5rem',
    position: 'relative',
    width: '100%',
  },
  createItemBtn: selectedRow
    ? {
        position: 'absolute',
        top: 8,
        right: 0,
        ml: 0,
      }
    : {},
});

const EXCLUDED_CLICK_FIELDS = ['edit', 'isActive', 'position', '__check__'];

export const MARKET_GROUP_PAGE_CONFIG = {
  [MARKET_TYPE_GROUP_DISPLAY_TYPE.MATCH]: {
    endpoint: API_ROUTES.MARKET_TYPE_GROUPS,
    queryKey: [QUERY_KEYS.marketTypeGroups, MARKET_TYPE_GROUP_DISPLAY_TYPE.MATCH],
  },
  [MARKET_TYPE_GROUP_DISPLAY_TYPE.TOURNAMENT]: {
    endpoint: API_ROUTES.MARKET_TYPE_GROUP_CATEGORIES,
    queryKey: [QUERY_KEYS.marketTypeGroupCategories, MARKET_TYPE_GROUP_DISPLAY_TYPE.TOURNAMENT],
  },
} as const;

const MarketGroupsPage = () => {
  const [selectedRow, setSelectedRow] = useState<MarketGroup | null>(null);
  const [selectedDisplayType, setSelectedDisplayType] = useState<MarketGroupDisplayType>(
    MARKET_TYPE_GROUP_DISPLAY_TYPE.MATCH
  );

  const tournamentMarketGroupsEnabled = useFeatureIsOn(FEATURE_FLAG.TOURNAMENT_MARKET_GROUPS);

  const {
    data: marketGroupsData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<MarketGroup>(MARKET_GROUP_PAGE_CONFIG[selectedDisplayType].endpoint, {
    page: 1,
    limit: 25,
    displayType: selectedDisplayType,
  });

  const { deleteData, updateData } = useMutateData(
    MARKET_GROUP_PAGE_CONFIG[selectedDisplayType].endpoint,
    MARKET_GROUP_PAGE_CONFIG[selectedDisplayType].queryKey
  );

  const { handleSort } = useSort(changeQuery);

  const styles = makeStyles(!!selectedRow);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', { sortable: false }),
      createSportIconColumn(),
      createColumn('sportName', 'Sport', {
        valueGetter: (value, row, column, apiRef) => row.sport.name,
      }),
      createColumn('name', 'Name'),
      createColumn('position', 'Position', {
        renderCell: (params) => (
          <MarketGroupsPositionInput
            defaultValue={params.row.position}
            onEnterClick={updateData}
            marketId={params.id as string}
          />
        ),
      }),
      createColumn('usedFor', 'Used for', {
        valueGetter: (value, row, column, apiRef) => formatSnakeCaseString(row.usedFor),
      }),
      createIsActiveColumn({
        updateData,
      }),
      createColumn('displayType', 'Display', {
        valueGetter: (value, row, column, apiRef) => capitalize(row.displayType),
      }),
      createEditColumn({
        deleteData,
        columnName: 'Actions',
      }),
    ],
    [deleteData, updateData]
  );

  const handleSearch = useCallback(
    (value: string | null) => {
      changeQuery({ search: value });
    },
    [changeQuery]
  );

  const hideGroupDetails = useCallback(() => {
    setSelectedRow(null);
  }, []);

  const onCellClick = useCallback((params: GridCellParams) => {
    if (!EXCLUDED_CLICK_FIELDS.includes(params.field)) {
      setSelectedRow(params.row);
    }
  }, []);

  const handleDisplayTypeToggle = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: MarketGroupDisplayType) => {
      if (value === null) return;

      setSelectedDisplayType(value);
      setSelectedRow(null);
      changeQuery({ displayType: value });
    },
    [changeQuery]
  );

  const selectedMarketGroupData = useMemo(
    () => marketGroupsData?.items.find((item) => item.id === selectedRow?.id),
    [marketGroupsData, selectedRow]
  );

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={marketGroupsData?.items || []}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={marketGroupsData?.count || 0}
        createItemBtnLabel='Add new market group'
        defaultVisibleColumns={defaultColumnsMarketGroups}
        handlePaginationModelChange={updateQueryParams}
        createItemBtnStyle={styles.createItemBtn}
        handleSearch={handleSearch}
        onCellClick={onCellClick}
        handleSort={handleSort}
        changeQuery={changeQuery}
        formType='market-groups'
        formProps={{ displayType: selectedDisplayType }}
        customTableHeaderContent={
          tournamentMarketGroupsEnabled ? (
            <ToggleButtonGroup value={selectedDisplayType} exclusive onChange={handleDisplayTypeToggle}>
              <ToggleButton value={MARKET_TYPE_GROUP_DISPLAY_TYPE.MATCH}>Match</ToggleButton>
              <ToggleButton value={MARKET_TYPE_GROUP_DISPLAY_TYPE.TOURNAMENT}>Tournament</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <></>
          )
        }
      />
      {selectedMarketGroupData && (
        <MobileDialogWrapper>
          <MarketGroupDetails handleClose={hideGroupDetails} marketGroupData={selectedMarketGroupData} />
        </MobileDialogWrapper>
      )}
    </Box>
  );
};

export default MarketGroupsPage;
